import { useState } from 'react';
import NumberedList from 'src/components/numberedList';
import BulletedList from 'src/components/bulletedList';
import config from 'config/config';
import Icon from 'src/components/icon';

const PROGRAM_URL = `${config.cdn.navigator}living-with-high-blood-pressure/`;

const getMenu = (personalisation) => {
  const personalisedSection = getPersonalisedSection(personalisation);

  return (
    <section className="navigator__contents">
      <div className="navigator__contents-header">Contents</div>
      {personalisedSection === 'ALCOHOL' && (
        <ul className="navigator__contents__list">
          <li><div className="navigator__contents__number">1.</div><div className="navigator__contents__content-text"><a href="#1">Alcohol and your heart</a></div></li>
          <li><div className="navigator__contents__number">2.</div><div className="navigator__contents__content-text"><a href="#2">Understanding alcohol guidelines</a></div></li>
          <li><div className="navigator__contents__number">3.</div><div className="navigator__contents__content-text"><a href="#3">How to start cutting back</a></div></li>
        </ul>
      )}
      {personalisedSection === 'NON_ALCOHOL' && (
        <ul className="navigator__contents__list">
          <li><div className="navigator__contents__number">1.</div><div className="navigator__contents__content-text"><a href="#1">Are drinks affecting your blood pressure?</a></div></li>
          <li><div className="navigator__contents__number">2.</div><div className="navigator__contents__content-text"><a href="#2">What about caffeinated drinks?</a></div></li>
          <li><div className="navigator__contents__number">3.</div><div className="navigator__contents__content-text"><a href="#3">Drinking hidden calories</a></div></li>
        </ul>
      )}
    </section>
  );
};

const personalisation_mapping = {
  k1: {
    v1: 'UNDER_30',
    v2: '30_55',
    v3: 'OVER_55',
  },
  k2: {
    v1: 'WHITE',
    v2: 'BLACK_ALL',
    v3: 'ASIAN_ALL',
    v4: 'MIXED',
    v5: 'OTHER',
  },
  k3: {
    v1: 'YES',
    v2: 'NO',
  },
  k4: {
    v1: 'YES',
    v2: 'NO',
  },
  c1: {
    v1: 'YES',
    v2: 'NO',
  },
};

const getPersonalisedSection = personalisation => {
  const drinks_alcohol = personalisation_mapping.k3[personalisation.k3];

  if (drinks_alcohol === 'YES') {
    return 'ALCOHOL';
  } else if (!drinks_alcohol) {
    return 'ALCOHOL';
  } else {
    return 'NON_ALCOHOL';
  }
};

const getPage = (personalisation) => {

  const personalisedSection = getPersonalisedSection(personalisation);

  return (
    <div className="navigator">
      <section className="navigator__content">

        {personalisedSection === 'ALCOHOL' && (
          <div>
            <h1 className="navigator__title">
              {'Week 6: Reducing alcohol and your blood pressure'}
            </h1>
            <p>{`Research shows that drinking too much alcohol on a regular basis will increase your blood pressure over time. If you have been diagnosed with high blood pressure and you drink often or in large quantities, cutting back or stopping drinking entirely will make a big difference to your health. This week, we'll show you how to make some changes that will improve your health.`}</p>
            <div className="visible-xs">{getMenu(personalisation)}</div>

            <h2>Alcohol and your heart</h2>
            <p>
              Drinking more than the recommended amount of alcohol can have a harmful effect on your blood pressure.
              As well as directly affecting blood pressure, alcohol can encourage other unhealthy habits, like smoking or eating salty and sugary foods.
              Alcohol is also high in calories so it can lead to weight gain, which can increase your risk of heart and circulatory disease too.
            </p>
            <p>{`You may hear myths about some drinks, like red wine being good for your heart, but there's actually no proof that one type of alcohol is 'better' for you than another.`}</p>
            <img alt="Alcoholic drinks" className="navigator__image-full" src={`${PROGRAM_URL}wk6a-alcohol.jpg`} />
            <a href="https://www.bhf.org.uk/heart-matters-magazine/medical/effects-of-alcohol-on-your-heart" rel="noreferrer" target="_blank">
              Learn more about the effects of alcohol on your heart {'>'}
            </a>

            <h2>Understanding alcohol guidelines</h2>
            <p>
              You should avoid drinking more than 14 units a week and those units should be spread out over at least 3 days.
              One drink can contain as many as 3 or 4 units.
            </p>
            <BulletedList items={[
              <span key="1">A <strong>unit</strong> is a measure of alcohol. The number of units in your drink is based on the size of the drink and its alcohol strength (ABV).</span>,
              <span key="2">The <strong>ABV</strong> (alcohol by volume) figure is the percentage of alcohol in the drink.</span>,
              <span key="3">A <strong>single pub measure</strong> (25mls) of spirits (40% ABV) contains one unit of alcohol.</span>,
              <span key="4">A <strong>small glass</strong> (50 ml) of liqueur, sherry or other fortified wine (20% ABV) contains one unit of alcohol.</span>,
              <span key="5"><strong>Half a pint</strong> (about 300mls) of normal strength (4% ABV) lager, cider or beer contains 1.1 unit of alcohol. But many beers and ciders are stronger and have a higher volume than this.</span>,
              <span key="6">A <strong>standard 175ml glass</strong> of wine (13% ABV) would be 2.3 units, and many wines have a higher alcohol content. You also need to be careful because a lot of glasses are bigger than 175ml.</span>]
            }
            />
            <a href="https://www.nhs.uk/Tools/Pages/drinks-tracker.aspx" rel="noreferrer" target="_blank">
              Download the NHS Drinks Tracker app onto your smartphone to stay aware of what you drink {'>'}
            </a>

            <h2>How to start cutting back</h2>
            <p>These 4 top tips will help you drink less and look after your heart.</p>
            <NumberedList items={[
              'Downsize. By switching from a large glass of wine to a small, or a pint to a half-pint, you can have what you want, but in moderation.',
              `Read the label. Some drinks are stronger than others. This will be shown by their % ABV (ABV stands for 'alcohol by volume'). Choose drinks with a lower % ABV.`,
              `Take a break. Try to have at least 3-4 alcohol-free days a week. And make sure you don't use the other days to 'catch up'.`,
              'Alternate alcoholic drinks with water or sugar-free soft drinks to stay hydrated and slow down your alcohol intake.']
            }
            />
            <a href="https://www.bhf.org.uk/heart-matters-magazine/nutrition/weight/calories-in-alcoholic-drinks" rel="noreferrer" target="_blank">
              How many calories are you drinking? Test your alcohol knowledge {'>'}
            </a>
          </div>
        )}

        {personalisedSection === 'NON_ALCOHOL' && (
          <div>
            <h1 className="navigator__title">
              {'Week 6: Non-alcoholic drinks and your blood pressure'}
            </h1>
            <p>{`If you've been told you have high blood pressure, you are probably looking at healthy changes to make to your diet. Make sure when you're looking for alternatives you don't forget about what you're drinking. This week will guide you through some non-alcoholic drinks you can cut back on so you can bring your blood pressure to a healthier level.`}</p>
            <div className="visible-xs">{getMenu(personalisation)}</div>

            <h2>Are drinks affecting your blood pressure?</h2>
            <p>{`If you drink sugary drinks, fruit juices, flavoured waters and smoothies, you're most likely drinking more sugar than you should. Lots of sugar in your diet can lead to weight gain, and being overweight can cause higher blood pressure. `}</p>
            <p>{`Just one large flavoured coffee can have over 30g of sugar in it - that's the maximum amount of free sugars you should have in a day! Free sugar means sugar that is added to food or drink - including honey, syrups and fruit juices.`}</p>
            <div className="navigator__video-container">
              <iframe
                allow="autoplay; encrypted-media; fullscreen"
                src="https://www.youtube.com/embed/hTX0iGAAwWY"
              />
            </div>
            <p>{`If you have sugar in your tea or coffee, reducing how much you use and eventually cutting it out is a great way to bring down your overall sugar intake. You don't have to do it overnight - little by little is one of the best ways to build lasting change. `}</p>

            <h2>What about caffeinated drinks?</h2>
            <p>
              People who are concerned about their blood pressure might worry about drinks that are high in caffeine like coffee,
              energy drinks, cola and some teas.
              Our BHF Senior Dietician Victoria Taylor offers some advice to let you know how much is too much.
            </p>
            <p>{`“Although drinking coffee has been shown to increase blood pressure, this effect is usually temporary and is minimised over time if you drink caffeinated drinks regularly. Some people are more sensitive to caffeine and can experience palpitations. If this is you, then it's sensible to avoid caffeine.”`}</p>
            <a href="https://www.bhf.org.uk/heart-matters-magazine/nutrition/ask-the-expert/how-much-caffeine" rel="noreferrer" target="_blank">
              Read more about caffeine and blood pressure {'>'}
            </a>

            <h2>Drinking hidden calories</h2>
            <section className="navigator__section-half">
              <p>
                The temptation to enjoy a new drink on the menu, a hot chocolate or sugary coffee is high.
                But, lots of these drinks contain a lot of sugar, and add huge amounts of calories to your daily intake and lead to weight gain.
              </p>
              <BulletedList items={[
                'Ditch the cream, marshmallows and drizzle - these are high in saturated fat and sugar.',
                `Always ask for the smallest size so you're drinking less but still having a treat.`,
                'Ask for skimmed milk and sugar-free syrup to cut calories down.']
              }
              />
            </section>
            <img alt="Hot chocolate with lots of sugar and marshmallows" className="navigator__image-half" src={`${PROGRAM_URL}wk6b_hotchoc.jpg`} />
            <a href="https://www.bhf.org.uk/heart-matters-magazine/nutrition/8-healthy-hot-drinks" rel="noreferrer" target="_blank">
              Find an alternative with our 8 healthy hot drinks  {'>'}
            </a>
          </div>
        )}

        <img alt="Content provided by BHF" className="navigator__image-full navigator__banner-footer" src={`${PROGRAM_URL}bhf-content.jpg`} />
      </section>
    </div>
  );
};

function Footer() {
  const [visible, setVisible] = useState(true);

  if (!visible) return null;
  return (
    <div className="navigator__footer">
      <img alt="HealthUnlocked British Heart Foundation community screenshot" className="navigator__footer__image" src={`${PROGRAM_URL}cta-community.png`} />
      <div className="navigator__footer__inner">
        <div className="navigator__footer__title">
          Join the conversation on HealthUnlocked
        </div>
        <div className="navigator__footer__description">
          {`To get the most out of your heart health journey, you'll need support. Our online community on HealthUnlocked brings patients, their loved-ones and healthcare experts all together online to share advice and encouragement.`}
          {' '}
          <a
            className="navigator__footer__link"
            href="https://healthunlocked.com/bhf"
            rel="noreferrer"
            target="_blank"
          >
            Join the conversation {'>'}
          </a>
        </div>
        <div className="navigator__footer__close" onClick={() => setVisible(false)}>
          <Icon icon="cross" />
        </div>
      </div>
    </div>
  );
}

const obj = {
  getPage,
  getMenu,
  Footer,
  getSeoTitle: 'Living with high blood pressure | HealthUnlocked',
};

export default obj;
